'use client'

import * as React from 'react'
import { Save, Bell, Globe, Database, Shield, Mail } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { Separator } from 'components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Badge } from 'components/ui/badge'
import { toast } from 'sonner'
import { useUser } from 'components/context/user-context'

export default function SettingsPage() {
  const { canEdit } = useUser()
  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
          System Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure platform-wide settings, integrations, and preferences
        </p>
      </div>

      <Tabs defaultValue="general" className="flex flex-col gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="general" className="gap-2 text-xs">
            <Globe className="size-3.5" />
            General
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2 text-xs">
            <Bell className="size-3.5" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2 text-xs">
            <Database className="size-3.5" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2 text-xs">
            <Shield className="size-3.5" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-foreground">General Configuration</CardTitle>
              <CardDescription className="text-xs">
                Basic platform settings and organizational defaults
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Institution name</Label>
                  <Input defaultValue="Jaramogi Oginga Odinga University of Science and Technology" className="h-9" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Short name</Label>
                  <Input defaultValue="JOOUST" className="h-9" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Default currency</Label>
                  <Select defaultValue="KES">
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KES">KES - Kenyan Shilling</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Fiscal year start</Label>
                  <Select defaultValue="july">
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="january">January</SelectItem>
                      <SelectItem value="april">April</SelectItem>
                      <SelectItem value="july">July</SelectItem>
                      <SelectItem value="october">October</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Timezone</Label>
                  <Select defaultValue="eat">
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eat">East Africa Time (UTC+3)</SelectItem>
                      <SelectItem value="utc">UTC</SelectItem>
                      <SelectItem value="gmt">GMT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
              <Button
                className="w-fit bg-primary text-primary-foreground hover:bg-accent gap-2"
                onClick={() => toast.success('Settings saved')}
                disabled={!canEdit}
              >
                <Save className="size-4" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-foreground">Notification Preferences</CardTitle>
              <CardDescription className="text-xs">
                Configure how and when the system sends notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              {[
                { label: 'Report due reminders', desc: 'Send reminders 7, 3, and 1 day before report deadlines', icon: <Mail className="size-4 text-primary" /> },
                { label: 'Risk escalation alerts', desc: 'Notify when risk scores exceed threshold', icon: <Shield className="size-4 text-destructive" /> },
                { label: 'MEL data submission reminders', desc: 'Remind users about pending indicator data submissions', icon: <Database className="size-4 text-warning" /> },
                { label: 'Approval workflow notifications', desc: 'Notify reviewers when items are pending approval', icon: <Bell className="size-4 text-success" /> },
                { label: 'ERP sync status alerts', desc: 'Notify admins when ERP integration syncs succeed or fail', icon: <Database className="size-4 text-muted-foreground" /> },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                  <div className="flex items-center gap-3">
                    {item.icon}
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>
              ))}
              <Button
                className="w-fit bg-primary text-primary-foreground hover:bg-accent gap-2 mt-2"
                onClick={() => toast.success('Notification preferences saved')}
                disabled={!canEdit}
              >
                <Save className="size-4" />
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-foreground">External Integrations</CardTitle>
              <CardDescription className="text-xs">
                Connect with institutional ERP and external systems
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-success/10">
                      <Database className="size-4 text-success" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Institutional ERP</p>
                      <p className="text-xs text-muted-foreground">Read-only budget sync via API</p>
                    </div>
                  </div>
                  <Badge className="bg-success/15 text-success text-[10px]">Connected</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground">API Endpoint</Label>
                    <Input defaultValue="https://erp.jooust.ac.ke/api/v1" className="h-8 text-xs" readOnly />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground">Sync frequency</Label>
                    <Select defaultValue="daily">
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-dashed border-border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-muted">
                      <Globe className="size-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Firebase Authentication</p>
                      <p className="text-xs text-muted-foreground">SSO via Google and Microsoft</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs text-foreground">Configure</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-foreground">Security & Access</CardTitle>
              <CardDescription className="text-xs">
                Configure security policies and access control
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              {[
                { label: 'Enforce MFA for all users', desc: 'Require multi-factor authentication on login' },
                { label: 'Session timeout (30 min)', desc: 'Automatically log out inactive users' },
                { label: 'IP whitelisting', desc: 'Restrict access to approved IP ranges only' },
                { label: 'Password complexity rules', desc: 'Require uppercase, lowercase, numbers, and symbols' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch defaultChecked={item.label.includes('MFA') || item.label.includes('Session')} />
                </div>
              ))}
              <Button
                className="w-fit bg-primary text-primary-foreground hover:bg-accent gap-2 mt-2"
                onClick={() => toast.success('Security settings saved')}
                disabled={!canEdit}
              >
                <Save className="size-4" />
                Save Security Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
